import React, { useEffect, useRef } from 'react';
import {
  i18n,
  useGetSo,
  useSessionStorage,
  INSURANCE_NEEDS_KEY,
  PROPERTY_INFO_KEY,
  CUSTOMER_DATA_KEY,
  SoDataInput,
  SoPropertyInfoInput,
  SoPropertyInsuranceNeedsInput,
  openCallMeModal,
  useNavigationPath,
  FlowEnum,
  useCreateSoWithCaptcha,
  useGeneralSoContext,
  useLoader,
  handleErrorMessage,
  showMessage,
  useCalculationInput,
} from 'src/common';
import { CaptchaChallenge, ContainerComponent, NavigationButtons, RequestAgentCardV2, StepHeader } from 'src/components';
import { InsuranceNeedsForm, InsuranceNeedsFormData, InsuranceNeedsFormType } from 'src/components/forms/insuranceNeeds.form';
import { FormRef } from 'src/components/forms/form';
import { NavigationService } from 'src/common/utils/navigation.util';
import { GetSoDto } from 'src/common';
import { Helmet } from 'react-helmet-async';

export const InsuranceNeedsPage: React.FC = () => {
  const { so, refetch } = useGetSo();
  const { soId, setSoId, soInitialData, setSoInitialData } = useGeneralSoContext();
  // In the webshop flow this is the session's first createSo (customer data was only
  // stored locally one step earlier), i.e. a creation the API may challenge with a
  // captcha. Later calls carry the soId and are free edits.
  const { submit: createSo, captcha } = useCreateSoWithCaptcha({ page: 'property-webshop-risks' });
  const { startLoader, stopLoader } = useLoader();
  const { setValue: setInsuranceNeeds, getValue: getInsuranceNeeds } = useSessionStorage<SoPropertyInsuranceNeedsInput>(INSURANCE_NEEDS_KEY);
  const { getValue: getPropertyInfo } = useSessionStorage<SoPropertyInfoInput>(PROPERTY_INFO_KEY);
  const { getValue: getCustomerData } = useSessionStorage<SoDataInput>(CUSTOMER_DATA_KEY);
  const { handleInsuranceNeeds } = useCalculationInput();
  const formRef = useRef<FormRef>(null);
  const { forwardPath, backwardPath, flow } = useNavigationPath();

  useEffect(() => {
    if (so) {
      refetch();
    }
  }, []);

  const handleBack = () => {
    NavigationService.navigate(backwardPath);
  };

  const handleFormSubmit = async (values: InsuranceNeedsFormData) => {
    const insuranceNeeds = {
      building: values.selectedPropertyIds?.includes('building') ?? false,
      personalProperty: values.selectedPropertyIds?.includes('household') ?? false,
      risks: values.selectedRiskIds ?? [],
    };

    setInsuranceNeeds(insuranceNeeds);

    if (flow === FlowEnum.WEBSHOP) {
      const data = (soInitialData?.data as SoDataInput | undefined) ?? getCustomerData() ?? {};
      const propertyInfo = (soInitialData?.propertyInfo as SoPropertyInfoInput | undefined) ?? getPropertyInfo() ?? undefined;

      startLoader();
      createSo(
        {
          input: {
            soId,
            data,
            productTypeSpecific: {
              insuranceNeeds: {
                ...insuranceNeeds,
                building: true,
                personalProperty: true
              },
              propertyInfo,
            },
          },
        },
        {
          onSuccess: ({ data: result }) => {
            if (result?.createSo) setSoId(result.createSo);
            setSoInitialData({ ...soInitialData, data, propertyInfo });
            handleInsuranceNeeds(insuranceNeeds.risks, true);
            NavigationService.navigate(forwardPath);
          },
          onError: (error) => {
            // RATE_LIMITED / IP_BLOCKED are already toasted by rateLimitErrorLink.
            const code = error.graphQLErrors?.[0]?.extensions?.code;
            if (code === 'RATE_LIMITED' || code === 'IP_BLOCKED') return;
            showMessage({
              type: 'error',
              message: handleErrorMessage(error),
              duration: 5000,
              fixed: true,
            });
          },
          onSettled: stopLoader,
        },
      );
      return;
    }

    NavigationService.navigate(forwardPath);
  };

  const modelToForm = (so?: GetSoDto): InsuranceNeedsFormData => {
    const insuranceNeeds = getInsuranceNeeds() ?? so?.productTypeSpecific?.insuranceNeeds;

    const propertyIds = [];
    if(insuranceNeeds?.building) {
      propertyIds.push('building')
    }
    
    if(insuranceNeeds?.personalProperty) {
      propertyIds.push('household')
    }

    return {
      selectedPropertyIds: propertyIds,
      selectedRiskIds: insuranceNeeds?.risks ?? [],
    }
  };

  const formData = modelToForm(so || undefined);

  const formItems = flow === FlowEnum.WEBSHOP ? 
    ['selectedRiskIds'] : ['selectedPropertyIds', 'selectedRiskIds']

  return (
    <ContainerComponent>
      <Helmet>
        <title>Potrebe osiguranja | Osiguranje imovine - kompare.hr</title>
      </Helmet>
      <StepHeader/>
      <InsuranceNeedsForm
        ref={formRef}
        data={formData}
        formItems={formItems as Array<InsuranceNeedsFormType>}
        onSubmit={handleFormSubmit}
      />
      {/* Appears only after the API asked for a captcha; solving it resubmits, so "next" waits. */}
      <CaptchaChallenge {...captcha} />
      <NavigationButtons
        handleBack={handleBack}
        handleNext={() => formRef.current?.submitForm?.()}
        nextDisabled={captcha.required}
        gap={64}
      />
      <RequestAgentCardV2
        title={i18n.t('components.cards.requestAgentCard.title')}
        onButtonClick={openCallMeModal}
      />
    </ContainerComponent>
  );
};

export default InsuranceNeedsPage;
